import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
  TrafficLayer,
  TransitLayer,
  BicyclingLayer,
} from "@react-google-maps/api";
import config from "../../config.js";
import { useSettings } from "../../context/SettingsContext";
import { DirectionsCache, createCacheKey } from "../dashboard/mapConstants.js";
import {
  getPlaceConfig,
  mapStyles,
  containerStyle,
  isValidMapCombination,
  normalizeMapView,
} from "../dashboard/functions.js";
import { useStopRedirectEnhanced } from "../../hooks/useUserInfo.js";
import { fitMapToStops } from "../dashboard/mapUtils.js";
import { getRightToLeftBusIcon } from "../../pages/stops/vehicleIcons.js";
import {
  calculateBearing,
  calculateDistance,
} from "../../utils/calculateDistance.js";
import {
  getUserDateFormat,
  getUserTimeFormat,
} from "../../utils/formatDate.js";

const libraries = ["places", "directions"];

const getUniqueItemsByKey = (items = [], getKey) => {
  const seen = new Set();

  return (items || []).filter((item) => {
    const key = getKey(item);

    if (key === "" || key === null || key === undefined || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const getVehicleDeviceKey = (vehicle) => {
  return String(vehicle?.device_id ?? vehicle?.deviceId ?? "");
};

const getStopKey = (stop) => {
  if (stop?.id !== undefined && stop?.id !== null) {
    return String(stop.id);
  }

  const latitude = stop?.latitude ?? "";
  const longitude = stop?.longitude ?? "";
  const name = stop?.name ?? "";

  return `${latitude}-${longitude}-${name}`;
};

function ReplayMap({
  stops = [],
  routes = [],
  vehicles = [],
  locationHistory = [],
  selectedTab = null,
}) {
  // console.log(stops,routes,vehicles,locationHistory)

  const { settings, loading } = useSettings();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const { redirectToStopTrack, orgId } = useStopRedirectEnhanced();

  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [vehicleDirections, setVehicleDirections] = useState({});
  const [placeMarkers, setPlaceMarkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const deviceIdToVehicles_color = useRef({});

  // Replay state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeIndex, setCurrentTimeIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [sortedLocationHistory, setSortedLocationHistory] = useState([]);
  const playbackIntervalRef = useRef(null);
  const currentTimeIndexRef = useRef(0);
  const sortedLocationHistoryRef = useRef([]);
  const lastStableHeadingRef = useRef({});
  const playbackCompletedRef = useRef(false);
  const MIN_HEADING_DISTANCE_METERS = 8;

  useEffect(() => {
    currentTimeIndexRef.current = currentTimeIndex;
  }, [currentTimeIndex]);

  useEffect(() => {
    sortedLocationHistoryRef.current = sortedLocationHistory;
  }, [sortedLocationHistory]);

  // Sort and process location history by timestamp
  useEffect(() => {
    if (!locationHistory || locationHistory.length === 0) {
      clearInterval(playbackIntervalRef.current);
      setIsPlaying(false);
      setCurrentTimeIndex(0);
      currentTimeIndexRef.current = 0;
      sortedLocationHistoryRef.current = [];
      lastStableHeadingRef.current = {};
      playbackCompletedRef.current = false;
      setIsLoading(false);
      return;
    }

    const allLocations = locationHistory
      .map((loc) => ({
        ...loc,
        device_id: loc.device_id,
        timestamp: new Date(loc.createdAt).getTime(),
        latitude: loc.latitude,
        longitude: loc.longitude,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    setSortedLocationHistory(allLocations);
    sortedLocationHistoryRef.current = allLocations;

    if (allLocations.length > 0) {
      clearInterval(playbackIntervalRef.current);
      setIsPlaying(false);
      setCurrentTimeIndex(0);
      currentTimeIndexRef.current = 0;
      lastStableHeadingRef.current = {};
      playbackCompletedRef.current = false;
      updateVehiclePositions(0, allLocations);
    }
    setIsLoading(false);
  }, [locationHistory]);

  // Calculate heading between two points
  const calculateHeadingBetweenPoints = useCallback(
    (prevPoint, currentPoint) => {
      if (!prevPoint || !currentPoint) return 0;

      const prevLat = parseFloat(prevPoint.latitude);
      const prevLng = parseFloat(prevPoint.longitude);
      const currLat = parseFloat(currentPoint.latitude);
      const currLng = parseFloat(currentPoint.longitude);
      const movedDistance = calculateDistance(
        { latitude: prevLat, longitude: prevLng },
        { latitude: currLat, longitude: currLng }
      );

      if (
        movedDistance === null ||
        Number.isNaN(movedDistance) ||
        movedDistance < MIN_HEADING_DISTANCE_METERS
      ) {
        return null;
      }

      return calculateBearing(
        { latitude: prevLat, longitude: prevLng },
        { latitude: currLat, longitude: currLng }
      );
    },
    []
  );

  // Improved vehicle position update with heading calculation
  const updateVehiclePositions = useCallback(
    (timeIndex, historyOverride = null) => {
      const history = historyOverride || sortedLocationHistoryRef.current;

      if (!history.length || timeIndex < 0) return;

      // Create maps to store current and previous positions for each device
      const currentPositions = new Map();
      const previousPositions = new Map();

      // Process all locations up to the current time index
      for (let i = 0; i <= timeIndex; i++) {
        // console.log(i,timeIndex)
        const location = history[i];
        if (location.device_id) {
          // Store the previous position before updating to current
          if (currentPositions.has(location.device_id)) {
            previousPositions.set(
              location.device_id,
              currentPositions.get(location.device_id)
            );
          }
          // Update current position
          currentPositions.set(location.device_id, location);
        }
      }

      // Update vehicle locations with calculated heading
      const newVehicleLocations = getUniqueItemsByKey(
        vehicles,
        getVehicleDeviceKey
      )
        .map((vehicle) => {
          const currentLocation = currentPositions.get(vehicle.device_id);
          if (!currentLocation) return null;

          const previousLocation = previousPositions.get(vehicle.device_id);
          const calculatedHeading = previousLocation
            ? calculateHeadingBetweenPoints(previousLocation, currentLocation)
            : null;
          const previousStableHeading =
            lastStableHeadingRef.current[vehicle.device_id] ?? 0;
          const heading =
            calculatedHeading === null
              ? previousStableHeading
              : calculatedHeading;

          lastStableHeadingRef.current[vehicle.device_id] = heading;

          return {
            ...vehicle,
            deviceId: vehicle.device_id,
            latitude: currentLocation.latitude.toString(),
            longitude: currentLocation.longitude.toString(),
            heading: heading,
            timestamp: currentLocation.timestamp,
          };
        })
        .filter(Boolean);
      setVehicleLocations(newVehicleLocations);
    },
    [vehicles, calculateHeadingBetweenPoints]
  );

  // Playback controls
  const startPlayback = useCallback(() => {
    const historyLength = sortedLocationHistoryRef.current.length;

    if (historyLength === 0) return;

    clearInterval(playbackIntervalRef.current);
    const isAtEnd = currentTimeIndexRef.current >= historyLength - 1;

    if (playbackCompletedRef.current || isAtEnd) {
      setCurrentTimeIndex(0);
      currentTimeIndexRef.current = 0;
      lastStableHeadingRef.current = {};
      playbackCompletedRef.current = false;
      updateVehiclePositions(0);
    }

    setIsPlaying(true);

    playbackIntervalRef.current = setInterval(() => {
      setCurrentTimeIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        const currentHistoryLength = sortedLocationHistoryRef.current.length;

        if (nextIndex >= currentHistoryLength) {
          setIsPlaying(false);
          clearInterval(playbackIntervalRef.current);
          playbackCompletedRef.current = true;
          return prevIndex;
        }

        currentTimeIndexRef.current = nextIndex;
        updateVehiclePositions(nextIndex);
        return nextIndex;
      });
    }, 1000 / playbackSpeed);
  }, [playbackSpeed, updateVehiclePositions]);

  const pausePlayback = useCallback(() => {
    setIsPlaying(false);
    clearInterval(playbackIntervalRef.current);
  }, []);

  const stopPlayback = useCallback(() => {
    setIsPlaying(false);
    clearInterval(playbackIntervalRef.current);
    setCurrentTimeIndex(0);
    currentTimeIndexRef.current = 0;
    lastStableHeadingRef.current = {};
    playbackCompletedRef.current = false;
    updateVehiclePositions(0);
  }, [updateVehiclePositions]);

  const seekToTime = useCallback(
    (timeIndex) => {
      const newIndex = Math.max(
        0,
        Math.min(timeIndex, sortedLocationHistory.length - 1)
      );
      setCurrentTimeIndex(newIndex);
      currentTimeIndexRef.current = newIndex;
      playbackCompletedRef.current =
        newIndex >= sortedLocationHistory.length - 1;
      updateVehiclePositions(newIndex);
    },
    [sortedLocationHistory, updateVehiclePositions]
  );

  // Handle playback speed changes
  useEffect(() => {
    if (isPlaying) {
      pausePlayback();
      startPlayback();
    }
  }, [playbackSpeed, isPlaying, pausePlayback, startPlayback]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(playbackIntervalRef.current);
    };
  }, []);

  // Populate deviceIdToVehicles_color
  useEffect(() => {
    const colorMap = {};
    getUniqueItemsByKey(vehicles, getVehicleDeviceKey).forEach((vehicle) => {
      colorMap[vehicle.device_id] = vehicle.vehicle_color || "green";
    });
    deviceIdToVehicles_color.current = colorMap;
  }, [vehicles]);

  const normalizeMapLayers = (mapLayers) => mapLayers?.toUpperCase() || "NONE";
  const mapType = normalizeMapView(settings.mapview);
  const mapLayers = normalizeMapLayers(settings.mapLayers);
  const uniqueVehicleLocations = getUniqueItemsByKey(
    vehicleLocations,
    getVehicleDeviceKey
  );
  const uniqueVehicles = getUniqueItemsByKey(vehicles, getVehicleDeviceKey);
  const uniqueStops = getUniqueItemsByKey(stops, getStopKey);

  // Fetch places using Places API
  useEffect(() => {
    if (!isLoaded || !map || !getPlaceConfig(mapLayers)) {
      setPlaceMarkers([]);
      return;
    }
    const placesService = new window.google.maps.places.PlacesService(map);
    let allResults = [];

    const request = {
      location: map.getCenter(),
      radius: 20000,
      type: getPlaceConfig(mapLayers).type,
    };

    const processResults = (results, status, pagination) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        results
      ) {
        const newMarkers = results.map((place) => ({
          id: place.place_id,
          name: place.name,
          position: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          },
        }));
        allResults = [...allResults, ...newMarkers];
        setPlaceMarkers(allResults);

        if (pagination && pagination.hasNextPage && allResults.length < 60) {
          setTimeout(() => pagination.nextPage(), 1000);
        }
      } else {
        console.warn(`Places API failed for ${mapLayers}: ${status}`);
        setPlaceMarkers([]);
      }
    };

    placesService.nearbySearch(request, processResults);
  }, [isLoaded, map, mapLayers]);

  const onLoad = useCallback(
    (map) => {
      setMap(map);
      if (vehicleLocations.length > 0 || stops.length > 0) {
        fitMapToStops(map, stops, vehicleLocations, {});
      }
    },
    [stops, vehicleLocations]
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (isLoaded && routes.length > 0 && map) {
      const directionsService = new window.google.maps.DirectionsService();

      const fetchDirections = async () => {
        const directionsPromises = routes.map((route) => {
          const cacheKey = createCacheKey(route, stops);
          return new Promise((resolve, reject) => {
            const cachedDirections = DirectionsCache.get(cacheKey);
            if (cachedDirections) {
              resolve({
                ...cachedDirections,
                routeColor: route.routeColor || cachedDirections.routeColor,
              });
              return;
            }

            const waypoints = route.routeCoordinates
              .slice(1, -1)
              .map((coord) => ({
                location: new window.google.maps.LatLng(
                  parseFloat(coord.latitude),
                  parseFloat(coord.longitude)
                ),
                stopover: coord.isStop || false,
              }));

            const origin = new window.google.maps.LatLng(
              parseFloat(route.routeCoordinates[0].latitude),
              parseFloat(route.routeCoordinates[0].longitude)
            );

            const destination = new window.google.maps.LatLng(
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .latitude
              ),
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .longitude
              )
            );

            directionsService.route(
              {
                origin,
                destination,
                waypoints,
                optimizeWaypoints: false,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  DirectionsCache.set(cacheKey, result, route.routeColor);
                  resolve({ ...result, routeColor: route.routeColor });
                } else {
                  reject(new Error(`Directions API error: ${status}`));
                }
              }
            );
          });
        });

        try {
          const results = await Promise.all(directionsPromises);
          const validResults = results.filter(
            (result) =>
              result &&
              result.routes &&
              result.routes.length > 0 &&
              result.routes[0].legs &&
              result.routes[0].legs.length > 0
          );
          setDirections(validResults);
        } catch (error) {
          console.error("Error fetching directions:", error);
          setDirections([]);
        }
      };

      fetchDirections();
    }
  }, [isLoaded, routes, map, stops]);

  const getDirectionArrow = (deviceId) => {
    const direction = vehicleDirections[deviceId];
    if (!direction || direction === "unknown") return "";
    let dir = direction === "source_to_dest" ? " ➡️" : " ⬅️";
    return dir;
  };

  const getVehicleIcon = (deviceId, heading) => {
    const color = deviceIdToVehicles_color.current[deviceId] || "green";
    const direction = vehicleDirections[deviceId] || "source_to_dest";
    const adjustedHeading =
      direction !== "source_to_dest" ? (heading + 180) % 360 : heading;
    const head = heading + 90;
    const rotatedSVG = getRightToLeftBusIcon(color, head).trim();
    const encodedSVG = encodeURIComponent(rotatedSVG);
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodedSVG}`,
      scaledSize: new window.google.maps.Size(45, 44),
      anchor: new window.google.maps.Point(22, 22),
    };
  };

  if (loadError) {
    console.error("Google Maps load error:", loadError);
    return <div>Error loading maps</div>;
  }
  if (!isLoaded || loading) {
    return (
      <div style={{ padding: "40px" }}>
        <div height="30vh" width="100%" />
      </div>
    );
  }
  function convertTo24Hour(time12h) {
    const [_, time, modifier] = time12h.split(" ");
    let [hours, minutes, seconds] = time.split(":");
    hours = parseInt(hours);
    if (modifier === "PM" && hours !== 12) {
      hours += 12;
    }
    if (modifier === "AM" && hours === 12) {
      hours = 0;
    }
    console.log(hours, minutes, seconds);
    // Format with leading zero if needed
    const hh = String(hours).padStart(2, "0");
    const mm = String(minutes).padStart(2, "0");
    const ss = String(seconds).padStart(2, "0");

    return `${hh}:${mm}:${ss}`;
  }

  const timeConverter = (timeStr) => {
    let timeFormat = getUserTimeFormat();
    if (timeFormat === "12hr") {
      return timeStr;
    } else {
      return convertTo24Hour(timeStr);
    }
  };

  const formatToDDMMYY = (dateStr) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    if (!dateStr) return "";
    let getFormat = getUserDateFormat();
    const ExtractDate = dateStr.split("/");
    let [year, month, day] = ExtractDate;
    if (getFormat === "DD/MM/YYYY") {
      return `${day}/${month}/${year}`;
    } else if (getFormat === "MM/DD/YYYY") {
      return `${month}/${day}/${year}`;
    } else if (getFormat === "YYYY/MM/DD") {
      return `${year}/${month}/${day}`;
    } else if (getFormat === "DD MMM YYYY") {
      return `${day} ${months[month - 1]} ${year}`;
    }
  };
  const formatTimestamp = (timestamp) => {
    // return new Date(timestamp).toLocaleString();
    // console.log(new Date(timestamp).toLocaleString()?.split(","))
    const extract = new Date(timestamp).toLocaleString()?.split(",");
    const date = extract[0];
    let time = timeConverter(extract[extract.length - 1]);
    let [month, day, year] = date?.split("/");
    const newValue = `${year}/${month}/${day}`;
    const getDate = formatToDDMMYY(newValue);
    if (
      getDate === "undefined/Invalid Date/undefined" &&
      time === "Invalid Date"
    ) {
      return "";
    } else {
      return `${getDate}, ${time}`;
    }
  };

  return (
    <>
      {/* Playback Controls */}
      <div
        style={{
          padding: "10px 20px",
          // backgroundColor: "#f5f5f5",
          // borderBottom: "1px solid #ddd",
          display: "flex",
          alignItems: "center",
          gap: "15px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={isPlaying ? pausePlayback : startPlayback}
          disabled={sortedLocationHistory.length === 0}
          title={isPlaying ? "Stop Replay" : "Start Replay"}
          aria-label={isPlaying ? "Stop Replay" : "Start Replay"}
          style={{
            // padding: "8px 16px",
            // backgroundColor: isPlaying ? "#ff6b6b" : "#20A8E0",
            // color: "white",
            border: "none",
            borderRadius: "13px",
            cursor:
              sortedLocationHistory.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          {isPlaying ? (
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="28" height="28" rx="14" fill="#20A8E0" />
              <path d="M10 9H13V19H10V9ZM15 9H18V19H15V9Z" fill="white" />
            </svg>
          ) : (
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="28" height="28" rx="14" fill="#199b48ff" />
              <path d="M11 8.5V19.5L19 14L11 8.5Z" fill="white" />
            </svg>
          )}
        </button>

        <button
          onClick={stopPlayback}
          disabled={sortedLocationHistory.length === 0}
          title="Reset Replay"
          aria-label="Reset Replay"
          style={{
            border: "none",
            cursor:
              sortedLocationHistory.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="28" height="28" rx="14" fill="#EE3B2B" />
            <path d="M9 9H19V19H9V9Z" fill="white" />
          </svg>
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flex: 1,
          }}
        >
          <>
            <input
              type="range"
              min="0"
              max={Math.max(0, sortedLocationHistory.length - 1)}
              value={currentTimeIndex}
              onChange={(e) => seekToTime(Number(e.target.value))}
              style={{
                flex: 1,
                height: "12px",
                borderRadius: "6px",
                outline: "none",
                background: `linear-gradient(to right, #183059 0%, #183059 ${
                  (currentTimeIndex /
                    Math.max(1, sortedLocationHistory.length - 1)) *
                  100
                }%, #e0e0e0 ${
                  (currentTimeIndex /
                    Math.max(1, sortedLocationHistory.length - 1)) *
                  100
                }%, #e0e0e0 100%)`,
                WebkitAppearance: "none",
                appearance: "none",
              }}
              disabled={sortedLocationHistory.length === 0}
            />
            <style
              dangerouslySetInnerHTML={{
                __html: `
    .custom-range-slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: white;
      border: 4px solid #20A8E0;
      cursor: pointer;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .custom-range-slider::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: white;
      border: 4px solid #20A8E0;
      cursor: pointer;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .custom-range-slider::-webkit-slider-thumb:hover {
      box-shadow: 0 2px 8px rgba(32, 168, 224, 0.4);
    }

    .custom-range-slider::-moz-range-thumb:hover {
      box-shadow: 0 2px 8px rgba(32, 168, 224, 0.4);
    }

    .custom-range-slider:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,
              }}
            />
          </>
          <span style={{ fontSize: "14px", minWidth: "200px" }}>
            {sortedLocationHistory.length > 0 ? (
              <>
                {formatTimestamp(
                  sortedLocationHistory[currentTimeIndex]?.timestamp
                )}
                {/* ({currentTimeIndex + 1} of {sortedLocationHistory.length}) */}
              </>
            ) : (
              "No location history available"
            )}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* <label htmlFor="playbackSpeed" style={{ fontSize: "14px" }}>
            Speed:
          </label> */}

          <select
            id="playbackSpeed"
            value={playbackSpeed}
            onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #B7BFCC",
              backgroundColor: "#F2F2F3",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontStyle: "medium",
              fontSize: "16px",
              color: "#1F242E",
            }}
          >
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={2}>2x</option>
            <option value={5}>5x</option>
            <option value={10}>10x</option>
            <option value={25}>25x</option>
            <option value={50}>50x</option>
            <option value={100}>100x</option>
          </select>
        </div>
      </div>

      <div
        style={{
          // padding: "20px 0",
          // border: "1px solid #183059",
          width: "100%",
          borderRadius: "8px",
        }}
      >
        <GoogleMap
          mapContainerStyle={containerStyle}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{ styles: mapStyles, mapTypeId: mapType }}
        >
          {mapLayers === "TRAFFIC" &&
            isValidMapCombination(settings.mapview, "TRAFFIC") && (
              <TrafficLayer />
            )}
          {mapLayers === "TRANSIT" &&
            isValidMapCombination(settings.mapview, "TRANSIT") && (
              <TransitLayer />
            )}
          {mapLayers === "BICYCLING" &&
            isValidMapCombination(settings.mapview, "BICYCLING") && (
              <BicyclingLayer />
            )}
          {mapLayers === "ROUTE_ONLY" && null}

          {getPlaceConfig(mapLayers) &&
            placeMarkers.map((place) => (
              <React.Fragment key={place.id}>
                <OverlayView
                  position={place.position}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      position: "relative",
                      top: "-62px",
                      left: "0px",
                      width: "2px",
                      height: "2px",
                    }}
                  ></div>
                </OverlayView>
              </React.Fragment>
            ))}

          {uniqueStops.map((stop) => (
            <React.Fragment key={getStopKey(stop)}>
              <OverlayView
                position={{
                  lat: parseFloat(stop.latitude),
                  lng: parseFloat(stop.longitude),
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{ position: "relative", top: "-62px", left: "0px" }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "white",
                      color: "black",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "500",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      whiteSpace: "nowrap",
                      zIndex: 1000,
                    }}
                  >
                    {stop.name}
                  </div>
                </div>
              </OverlayView>
              <Marker
                position={{
                  lat: parseFloat(stop.latitude),
                  lng: parseFloat(stop.longitude),
                }}
                options={{ cursor: "pointer" }}
                title={stop.name}
              />
            </React.Fragment>
          ))}

          {uniqueVehicleLocations.map((vehicle) => {
            const vehicleData =
              uniqueVehicles.find((v) => v?.device_id === vehicle?.deviceId) ||
              {};
            const vehicleIcon = getVehicleIcon(
              vehicle.deviceId,
              vehicle?.heading || 0
            );
            const vehicleName = vehicleData.name;

            return (
              vehicle &&
              vehicle.latitude &&
              vehicle.longitude && (
                <React.Fragment key={vehicle.deviceId}>
                  <OverlayView
                    position={{
                      lat: parseFloat(vehicle.latitude),
                      lng: parseFloat(vehicle.longitude),
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      style={{
                        position: "relative",
                        top: "13px",
                        left: "0px",
                        width: "2px",
                        height: "2px",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "10px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          color: "#fff",
                          fontSize: "8px",
                          fontWeight: 600,
                          fontFamily: "Arial, sans-serif",
                          lineHeight: "12px",
                          padding: "6px",
                          textAlign: "left",
                          whiteSpace: "nowrap",
                          backgroundColor: "#008C23",
                          borderRadius: "10px",
                          zIndex: 1000,
                          border: "1px solid black",
                        }}
                      >
                        {vehicleName}
                        <span style={{ fontWeight: "bold" }}>
                          {getDirectionArrow(vehicle.deviceId)}
                        </span>
                      </div>
                    </div>
                  </OverlayView>
                  <Marker
                    position={{
                      lat: parseFloat(vehicle.latitude),
                      lng: parseFloat(vehicle.longitude),
                    }}
                    icon={vehicleIcon}
                  />
                </React.Fragment>
              )
            );
          })}

          {directions.map((direction, index) => (
            <DirectionsRenderer
              key={index}
              directions={direction}
              options={{
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                  strokeColor: direction.routeColor || "#000000",
                  strokeOpacity: 0.8,
                  strokeWeight: 4,
                },
              }}
            />
          ))}
        </GoogleMap>
      </div>
    </>
  );
}

export default React.memo(ReplayMap);
